/**
 * Where a tab reads its live notifications from, and which it has already handled.
 *
 * Tabs don't delete the notifications they apply: the same user's other tabs (one hidden, its listener
 * paused) may still need them, and a TTL removes them a day after they were written. So each tab keeps its
 * own place. It reads from a little before the newest notification it handled, because a notification's
 * time is when it was raised and one can be written a moment after a newer one, and it skips any it already
 * handled.
 */
export class NotificationCursor {
    /**
     * `windowSize`: how many new notifications a read brings in; `maxReadSize`: the largest read, counting those
     * read again (handled already) from where it starts.
     */
    constructor(startUtc, lateWindowMs, windowSize, maxReadSize) {
        this.lateWindowMs = lateWindowMs;
        this.windowSize = windowSize;
        this.maxReadSize = maxReadSize;
        /** Handled notification ids and their times, back to where the current read started. */
        this.handled = new Map();
        this.newest = startUtc;
        this.readFrom = startUtc;
        this.readSize = windowSize;
    }
    /** Where the current read starts (inclusive). */
    get start() {
        return this.readFrom;
    }
    /** How many the current read takes from its start: those handled there already (read again), and a window. */
    get limit() {
        return this.readSize;
    }
    /** Whether the tab hasn't handled this notification yet; from now on it has. */
    take(id, createdAt) {
        if (this.handled.has(id))
            return false;
        this.handled.set(id, createdAt);
        if (createdAt > this.newest)
            this.newest = createdAt;
        return true;
    }
    /**
     * A read came back full: read on from just before its newest notification, where one written late can still
     * come in, taking a window more than is handled there. Never past that margin. When the read would be larger
     * than `maxReadSize` (a burst within the margin), it goes past the burst instead and returns false: what was
     * written late in it may be missed, and the caller reads its data again.
     */
    afterFullRead() {
        this.moveTo(Math.max(this.readFrom, this.newest - this.lateWindowMs));
        const size = this.handled.size + this.windowSize;
        if (size <= this.maxReadSize) {
            this.readSize = size;
            return true;
        }
        this.moveTo(this.newest + 1);
        this.readSize = this.windowSize;
        return false;
    }
    /** Reading again after a pause: from just before the newest handled notification, to catch any written late. */
    afterPause() {
        this.moveTo(Math.max(this.readFrom, this.newest - this.lateWindowMs));
        this.readSize = Math.min(this.handled.size + this.windowSize, this.maxReadSize);
    }
    moveTo(start) {
        this.readFrom = start;
        // Notifications before the read's start can't come back: forget them.
        for (const [id, createdAt] of this.handled) {
            if (createdAt < start)
                this.handled.delete(id);
        }
    }
}
